import type { ReactNode } from "react";
import { Box, HStack, Image, Stack, Text, Button } from "@chakra-ui/react";
import { useMemo } from "react";
import type { CreativeBlock } from "../guidedFlow/types";
import type { FacebookPage, InstagramAccount } from "../types";
import CreativeIdentityReviewRows from "./CreativeIdentityReviewRows";
import { buildCreativeBlockIdentityReviewRows } from "../utils/buildCreativeIdentityReviewRows";
import {
  hasSquareCreativeSlot,
  hasVerticalCreativeSlot,
} from "../guidedFlow/creativePreviewSlots";

export type CreativeBlockReviewProps = {
  creative: CreativeBlock;
  index: number;
  onEdit: () => void;
  launching?: boolean;
  pages: readonly FacebookPage[];
  instagramAccounts: readonly InstagramAccount[];
  partnerPostsEnabled?: boolean;
};

const imageHeight = "150px" as const;

function MediaSlotBox({
  label,
  aspectLabel,
  children,
  fullWidth,
}: {
  label: string;
  aspectLabel: string;
  children: ReactNode;
  fullWidth?: boolean;
}) {
  return (
    <Box
      flex={1}
      aspectRatio={1}
      alignContent="center"
      w={fullWidth ? "full" : "50%"}
      bg="gray.50"
      rounded="lg"
      p={2}
      border="1px dotted"
      borderColor="gray.300"
      textAlign="center"
    >
      <Text fontSize="sm" color="gray.500" fontWeight="semibold" mb={2}>
        <Text as="span" color="gray.600">
          {label}{" "}
        </Text>
        {aspectLabel}
      </Text>
      <Box mx="auto" w="fit-content" shadow="lg">
        {children}
      </Box>
    </Box>
  );
}

export default function CreativeBlockReview({
  creative,
  onEdit,
  launching = false,
  pages,
  instagramAccounts,
  partnerPostsEnabled = false,
}: CreativeBlockReviewProps) {
  const showSquare =
    hasSquareCreativeSlot(creative) && Boolean(creative.squareMediaUrl?.trim());
  const showVertical =
    hasVerticalCreativeSlot(creative) &&
    Boolean(creative.verticalMediaUrl?.trim());
  const singleMediaSlot = showSquare !== showVertical;

  const identityRows = useMemo(
    () =>
      buildCreativeBlockIdentityReviewRows(
        creative,
        pages,
        instagramAccounts,
        partnerPostsEnabled
      ),
    [creative, pages, instagramAccounts, partnerPostsEnabled]
  );

  return (
    <Box
      bg="white"
      rounded="lg"
      p={4}
      border="1px solid"
      borderColor="gray.200"
    >
      <HStack>
        <Text fontSize="sm" color="gray.800" fontWeight="semibold" mb={4}>
          {creative.name || "-"}
        </Text>
        <Text fontSize="xs" color="gray.500" mb={4}>
          Single-format or multi-format
        </Text>
      </HStack>

      {(showSquare || showVertical) && (
        <Stack
          direction={{ base: "column", sm: singleMediaSlot ? "column" : "row" }}
          align={singleMediaSlot ? "center" : "stretch"}
          spacing={4}
          mb={4}
        >
          {showSquare && (
            <MediaSlotBox
              label="Square"
              aspectLabel="1:1"
              fullWidth={!singleMediaSlot}
            >
              {creative.squareMediaType === "video" ? (
                <Box
                  as="video"
                  src={creative.squareMediaUrl}
                  h={imageHeight}
                  aspectRatio={1}
                  objectFit="cover"
                  autoPlay
                  muted
                  loop
                  controls
                />
              ) : (
                <Image
                  src={creative.squareMediaUrl}
                  alt=""
                  h={imageHeight}
                  aspectRatio={1}
                  objectFit="cover"
                />
              )}
            </MediaSlotBox>
          )}
          {showVertical && (
            <MediaSlotBox
              label="Vertical"
              aspectLabel="16:9"
              fullWidth={!singleMediaSlot}
            >
              {creative.verticalMediaType === "video" ? (
                <Box
                  as="video"
                  src={creative.verticalMediaUrl}
                  aspectRatio={9 / 16}
                  h={imageHeight}
                  objectFit="cover"
                  autoPlay
                  muted
                  loop
                  controls
                />
              ) : (
                <Image
                  src={creative.verticalMediaUrl}
                  alt=""
                  aspectRatio={9 / 16}
                  h={imageHeight}
                  objectFit="cover"
                />
              )}
            </MediaSlotBox>
          )}
        </Stack>
      )}

      <Box mb={4}>
        <HStack justify="space-between" align="flex-start" mb={1}>
          <Text fontSize="sm" fontWeight="semibold" color="gray.600">
            Primary Text
          </Text>
          <Button
            variant="link"
            fontSize="sm"
            fontWeight="medium"
            color="blue.600"
            onClick={onEdit}
            cursor="pointer"
            _hover={{ textDecoration: "underline" }}
            flexShrink={0}
            isDisabled={launching}
          >
            Edit
          </Button>
        </HStack>
        <Text fontSize="xs" color="gray.600" noOfLines={4}>
          {creative.primaryText || "—"}
        </Text>
      </Box>

      <Box mb={4}>
        {creative.link?.trim() ? (
          <>
            <HStack justify="space-between" align="flex-start" mb={1}>
              <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                Link
              </Text>
              <Button
                variant="link"
                fontSize="sm"
                fontWeight="medium"
                color="blue.600"
                onClick={onEdit}
                cursor="pointer"
                _hover={{ textDecoration: "underline" }}
                flexShrink={0}
                isDisabled={launching}
              >
                Edit
              </Button>
            </HStack>
            <Text fontSize="sm" color="gray.700" noOfLines={2}>
              {creative.link.trim()}
            </Text>
          </>
        ) : (
          <HStack justify="space-between" align="center" spacing={3}>
            <HStack spacing={2} align="baseline" minW={0} flexWrap="wrap">
              <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                Event link
              </Text>
              <Text fontSize="xs" color="gray.500">
                This defaults to the event page
              </Text>
            </HStack>
            <Button
              variant="link"
              fontSize="sm"
              fontWeight="medium"
              color="blue.600"
              onClick={onEdit}
              cursor="pointer"
              _hover={{ textDecoration: "underline" }}
              flexShrink={0}
              isDisabled={launching}
            >
              Edit
            </Button>
          </HStack>
        )}
      </Box>

      <CreativeIdentityReviewRows
        rows={identityRows}
        onEditAction={onEdit}
        editActionLabel="Change"
        editDisabled={launching}
      />
    </Box>
  );
}
